import { cn } from '../../lib/cn'

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive' | 'accent'
export type ButtonSize = 'sm' | 'md' | 'lg'

const BASE = [
  'relative inline-flex items-center justify-center gap-2',
  'touch-target', // 44px minimum, straight from the a11y tokens
  'rounded-md font-semibold whitespace-nowrap',
  /*
   * ⚠️ `transform` IS IN THIS LIST AND `btn-motion` IS THE OTHER HALF.
   *
   * This used to transition colour, border, shadow and nothing else, so a
   * button changed hue and never moved — including on TAP, where there is no
   * hover at all and an inert control reads as a broken one. `.btn-motion`
   * (globals.css) adds a 1 px lift on hover and a 0.97 press on `:active`.
   *
   * The two halves are split deliberately. The transition lives here because
   * it is part of what a button IS; the transforms live in the stylesheet
   * because that is where `prefers-reduced-motion` and `hover: hover` are
   * enforced for the whole site, and a transform written inline here would
   * sit outside both gates. Under reduced motion the button still changes
   * colour — colour is information, not motion.
   *
   * ⚠️ NOTHING HERE MAY ANIMATE A BOX DIMENSION. Buttons sit in flex rows in
   * pairs all over this site; a width, padding or margin change reflows the
   * row and shoves the button next to it sideways.
   */
  'btn-motion',
  'transition-[background-color,border-color,box-shadow,color,transform] duration-hover ease-out',
  /*
   * ⚠️ MEASURED, NOT ASSUMED: a `<button>` on this site computes
   * `cursor: default`. Tailwind v4's preflight no longer gives buttons a
   * pointer cursor the way v3 did, so every `<a>` here says "clickable" and
   * every `<button>` says nothing. It is also the documented prerequisite for
   * `:active` to apply on tap in iOS Safari — which is the browser where the
   * press state below is most likely to go missing.
   */
  'cursor-pointer',
  'disabled:pointer-events-none disabled:opacity-50',
  'aria-busy:cursor-progress',
].join(' ')

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-primary-foreground shadow-1 hover:bg-primary/90 hover:shadow-brand',
  secondary: 'bg-secondary text-secondary-foreground border border-border hover:bg-muted',
  outline: 'border border-input bg-transparent text-foreground hover:bg-muted',
  ghost: 'bg-transparent text-foreground hover:bg-muted',
  destructive: 'bg-destructive text-destructive-foreground shadow-1 hover:bg-destructive/90',
  accent: 'bg-accent text-accent-foreground shadow-1 hover:bg-accent/90',
}

/**
 * Heights come from the `touch-target` minimum rather than a fixed `h-*`, so a
 * small button is still tappable on a phone while staying visually compact.
 */
const SIZES: Record<ButtonSize, string> = {
  sm: 'px-3.5 py-2 type-body-sm',
  md: 'px-5 py-2.5 type-body',
  lg: 'px-6 py-3 type-body-lg rounded-lg',
}

export const buttonClasses = (
  variant: ButtonVariant = 'primary',
  size: ButtonSize = 'md',
  className?: string,
): string => cn(BASE, VARIANTS[variant], SIZES[size], className)
